import { Controller } from '@hotwired/stimulus';
import { WidgetManager } from '@infakt/widgets/vanilla-sdk';
import { checkIfWebView } from '@infakt/webview-toolkit';
import { trackError } from '../shared/lib/errorTracking';

// User.com loads widget.js asynchronously, so poll until its SDK exposes window.userengage.
const CHAT_RETRY_MS = 250;
const CHAT_TIMEOUT_MS = 10000;

export default class extends Controller {
  static values = {
    name: String,
    containerId: String,
  };

  static targets = ['errorContainer'];

  async connect() {
    this.hideChatWidget();

    try {
      this.manager = new WidgetManager();
      this.widget = await this.manager.init({
        name: this.nameValue,
        containerId: this.containerIdValue,
        onError: (error) => {
          trackError(
            error,
            {
              context: 'business_onboarding.widget_internal',
              name: this.nameValue,
              containerId: this.containerIdValue,
              isWebView: checkIfWebView(),
            },
            {
              errorMessage: error.message,
            },
          );
        },
      });

      await this.widget.mount();
      // Adjust the widget height for mobile browsers after widget is mounted
      this.changeWidgetHeight();
    } catch (error) {
      this.showErrorAlert();
      trackError(
        error,
        {
          context: 'business_onboarding.widget_init',
          name: this.nameValue,
          containerId: this.containerIdValue,
          isWebView: checkIfWebView(),
        },
        {
          errorMessage: error.message,
        },
      );
    }
  }

  showErrorAlert() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.remove('hidden');
    }
  }

  reloadPage() {
    window.location.reload();
  }

  changeWidgetHeight() {
    // Find the widget element in the container
    const container = document.getElementById(this.containerIdValue);
    if (!container) return;

    const shadowRoot = container.shadowRoot;
    if (!shadowRoot) return;

    if (checkIfWebView()) {
      document.body.style.overflow = 'hidden';
      const style = document.createElement('style');
      style.textContent = `
        #infakt-widget-shadow-content .iw-widget > div:first-of-type {
          height: 100dvh !important;
        }
      `;
      shadowRoot.appendChild(style);

      return;
    }

    const style = document.createElement('style');
    style.textContent = `
        @media screen and (max-width: 767px) {
          #infakt-widget-shadow-content .iw-widget > div:first-of-type {
            height: calc(100dvh - var(--iw-header-mobile-height, 0px)) !important;
          }
        }
      `;
    shadowRoot.appendChild(style);
  }

  disconnect() {
    clearTimeout(this.chatTimer);
    this.callUserengage('widget.show');
    this.cleanup();
  }

  hideChatWidget(deadline = Date.now() + CHAT_TIMEOUT_MS) {
    if (this.callUserengage('widget.hide') || Date.now() > deadline) return;

    this.chatTimer = setTimeout(() => this.hideChatWidget(deadline), CHAT_RETRY_MS);
  }

  callUserengage(action) {
    if (typeof window.userengage !== 'function') return false;

    window.userengage(action);

    return true;
  }

  cleanup() {
    if (this.element) {
      if (this.widget && this.widget.isMounted()) {
        this.widget.unmount();

        if (checkIfWebView()) {
          document.body.style.overflow = '';
        }
      }
      this.element.innerHTML = '';
    }
  }
}
